import { buildSearchQuery } from '@/utils/search/searchQuery';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { debounce } from 'es-toolkit';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getArchivedSearchFacets,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
    getUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { H5 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { memo, ReactElement, ReactNode, useCallback, useState } from 'react';
import { SlidersHorizontal } from '@liveauctioneers/hammer-ui-core/icons';
import { sortCountryFacets } from '@/utils/search/searchUtils';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import AdContainer from '@/components/Ads/AdContainer';
import AuctionDateFacet from '@/components/Facets/AuctionDateFacet';
import FacetTypes from '@/types/search/enums/facetTypes';
import isEqual from 'react-fast-compare';
import LocationShippingFacet from '@/components/Facets/components/LocationShippingFacet/LocationShippingFacet';
import MultiCheckFacet from '@/components/Facets/MultiCheckFacet';
import RangeFacet from '@/components/Facets/RangeFacet';
import ResultsCount from '@/pages/Search/Components/ResultsCount/ResultsCount';
import SearchInput from '@liveauctioneers/caterwaul-components/lib/Input/SearchInput/SearchInput';
import styled, { css } from 'styled-components';
import TaxonomyFacet from '@/components/Facets/TaxonomyFacet';

export type SearchFacetsProps = {
    className?: string;
    clearAllParamsExceptCategories: (event: any) => void;
    distanceSelected: (countryName: string, zipCode: string, range: number, measurement: string) => void;
    /**
     * Whether or not there are any filters applied to the current search
     */
    empty?: boolean;
    excludedFacets: string[];
    facetKeywordChanged?: Function;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    filterBar?: ReactNode | null;
    isLandingPage?: boolean;
    isMobileView?: boolean;
    pageName: string;
    rangeEntered: Function;
    removeCountryAndDistance: (event?: any) => void;
    replaceCountry: (countryId: string) => void;
    showAllFacets?: string[];
    showLandingPageLinks?: boolean;
    sortFilter?: ReactElement | null;
    taxonomyParentSelected: Function;
    toggleFilterTerm: (term: string) => void;
    userIsLoggedIn?: boolean;
};

const SearchFacets = ({
    className,
    clearAllParamsExceptCategories,
    distanceSelected,
    empty,
    excludedFacets,
    facetOptionChecked,
    filterBar,
    isLandingPage,
    isMobileView = false,
    pageName,
    rangeEntered,
    removeCountryAndDistance,
    replaceCountry,
    showAllFacets = [],
    showLandingPageLinks,
    sortFilter,
    taxonomyParentSelected,
    toggleFilterTerm,
    userIsLoggedIn = false,
}: SearchFacetsProps) => {
    const intl = useIntl();
    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams(search);
    const archivedSearch = isArchivedSearch(queryParams, pathname);

    const [newKeywordValue, setNewKeywordValue] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const pageSize = useAppSelector(getSearchPageSize);
    const archivedSearchFacets = useAppSelector(getArchivedSearchFacets);
    const archivedSearchRawFacets = useAppSelector(getRawArchivedSearchFacets);
    const upcomingFacets = useAppSelector(getUpcomingSearchFacets);
    const upcomingRawFacets = useAppSelector(getRawUpcomingSearchFacets);
    const { totalFound } = useAppSelector(getSearchUiState);

    const facets = archivedSearch ? archivedSearchFacets : upcomingFacets;

    const searchQuery = buildSearchQuery({
        pathname,
        queryParams,
        rawFacets: archivedSearch ? archivedSearchRawFacets : upcomingRawFacets,
        searchPageSize: pageSize,
    });

    const { distance, ranges } = searchQuery;

    const handleDrawerOpen = useCallback(() => {
        setIsDrawerOpen(true);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    const handleClearAll = useCallback(
        (event: any) => {
            clearAllParamsExceptCategories(event);
            handleDrawerClose();
        },
        [clearAllParamsExceptCategories, handleDrawerClose]
    );

    const mapFacets = useCallback(
        (facet, index) => {
            const { categories, excludable, id, name, options, range } = facet;

            const { formatMessage } = intl;

            if (excludedFacets.includes(id)) {
                // Don't list the excluded facets (This allows for sharing one component across search types like search page and catalog page)
                return;
            }

            // if true all facets will be shown and "show all" link will not be present
            const showAll = showAllFacets.includes(id);

            const title = formatMessage({ id: `search_facet_title_${id}` });

            if (options) {
                const showSearchHeader = name === 'Auction House' || name === 'Creator facet';
                const searchPlaceholder =
                    name === 'Auction House'
                        ? intl.formatMessage({ id: 'searchByHousePlaceholder' })
                        : name === 'Creator facet'
                          ? intl.formatMessage({
                                id: 'searchByCreatorPlaceholder',
                            })
                          : '';
                const loadPageWithFacetCollapsed = true;
                const emptyMessage =
                    id === FacetTypes.AUCTION_HOUSE_ID ? (
                        <FormattedMessage
                            id="search_no_auction_house_found"
                            key="no_ah_found"
                        />
                    ) : (
                        <FormattedMessage
                            id="search_no_creator_found"
                            key="no_creator_found"
                        />
                    );
                return whiteLabel && name === 'Auction House' ? null : (
                    <MultiCheckFacet
                        checklist={options}
                        emptyMessage={emptyMessage}
                        excludable={excludable}
                        facetId={id}
                        facetOptionChecked={facetOptionChecked}
                        isMobileView={isMobileView}
                        key={index}
                        loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                        queryParams={queryParams}
                        searchPlaceholder={searchPlaceholder}
                        showAll={showAll}
                        showLandingPageLinks={showLandingPageLinks}
                        showSearchHeader={showSearchHeader}
                        title={title}
                        userIsLoggedIn={userIsLoggedIn}
                    />
                );
            } else if (id === FacetTypes.SALE_START) {
                const loadPageWithFacetCollapsed = true;
                return (
                    <AuctionDateFacet
                        key={index}
                        loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                        onChangeYearRange={rangeEntered}
                    />
                );
            } else if (range && (typeof range.min === 'number' || typeof range.max === 'number')) {
                const min = ranges[id] && Number(ranges[id].min) !== -1 ? range.min : '';
                const max = ranges[id] && Number(ranges[id].max) !== -1 ? range.max : '';
                const rangeForm = { max, min };
                const loadPageWithFacetCollapsed = true;
                return (
                    <RangeFacet
                        facetId={id}
                        key={index}
                        loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                        range={rangeForm}
                        rangeEntered={rangeEntered}
                        title={title}
                    />
                );
            } else if (categories) {
                const loadPageWithFacetCollapsed = name !== 'Category facet';
                return (
                    <TaxonomyFacet
                        category={categories}
                        facetId={id}
                        key={index}
                        loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                        queryParams={queryParams}
                        showLandingPageLinks={showLandingPageLinks}
                        taxonomyParentSelected={taxonomyParentSelected}
                        title={title}
                    />
                );
            }

            return null;
        },
        [
            excludedFacets,
            facetOptionChecked,
            intl,
            isMobileView,
            queryParams,
            rangeEntered,
            ranges,
            showAllFacets,
            showLandingPageLinks,
            taxonomyParentSelected,
            userIsLoggedIn,
            whiteLabel,
        ]
    );
    // don't include country or shipping because they will be combined in the LocationShippingFacet later
    let facetElements = facets
        .filter((facet) => facet.id !== FacetTypes.COUNTRY_CODE_ID && facet.id !== FacetTypes.SHIPPING)
        .map((facet, index) => mapFacets(facet, index));

    if (!excludedFacets.includes(FacetTypes.LOCATION_ID)) {
        const countryFacet = facets.filter((facet) => facet.id === FacetTypes.COUNTRY_CODE_ID)[0];
        const shippingFacet = facets.filter((facet) => facet.id === FacetTypes.SHIPPING)[0];
        const sortedAvailableCountries = Boolean(countryFacet) ? sortCountryFacets(countryFacet.options) : [];

        if (facetElements.length > 0 && totalFound > 0 && Boolean(countryFacet)) {
            const locationShipping = (
                <LocationShippingFacet
                    availableCountries={sortedAvailableCountries}
                    countries={countryFacet?.options}
                    distance={distance}
                    distanceSelected={distanceSelected}
                    facetOptionChecked={facetOptionChecked}
                    key="countryCode"
                    loadPageWithFacetCollapsed
                    removeCountryAndDistance={removeCountryAndDistance}
                    replaceCountry={replaceCountry}
                    shippingFacet={shippingFacet}
                    title={intl.formatMessage({
                        id: 'search_facet_title_locationAndShipping',
                    })}
                />
            );
            // Location facet goes second
            facetElements.splice(1, 0, locationShipping);
        }
    }

    const showAdContainer = !whiteLabel && totalFound > 0;

    const facetWrapper = (
        <StyledFacetWrapper
            $isMobileFacets={isMobileView}
            className={className}
            // @ts-expect-error preventclose is not a real attribute, according to TypeScript
            //   and yet, it prevents the slider from closing while interacting with the menu on mobile 🤷‍
            preventclose="true"
        >
            {isMobileView && (
                <>
                    <SortAndKeywordsContainer>
                        <div>{sortFilter}</div>
                        <FilterBar
                            $displaySortBy={!archivedSearch}
                            $empty={empty}
                        >
                            {filterBar}
                        </FilterBar>
                    </SortAndKeywordsContainer>
                    <ResultsAndSearchContainer>
                        <ResultsCountRow>
                            <ResultsCountLabel>
                                <ResultsCount
                                    isPriceResults={archivedSearch}
                                    totalFound={totalFound}
                                />
                            </ResultsCountLabel>
                        </ResultsCountRow>
                        <StyledSearchInput>
                            <SearchInput
                                aria-label={intl.formatMessage({
                                    id: 'ariaLabel.searchInput',
                                })}
                                controlled
                                hideButton
                                onChange={(event) => {
                                    setNewKeywordValue(event.target.value);
                                }}
                                onSubmit={debounce((keyword) => {
                                    toggleFilterTerm(keyword);
                                    setNewKeywordValue('');
                                }, 300)}
                                placeholder={intl.formatMessage({
                                    id: 'filter_by_keyword',
                                })}
                                small
                                value={newKeywordValue}
                                wide
                            />
                        </StyledSearchInput>
                    </ResultsAndSearchContainer>
                </>
            )}
            {!isMobileView && (
                <StyledSearchInput>
                    <SearchInput
                        aria-label={intl.formatMessage({
                            id: 'ariaLabel.searchInput',
                        })}
                        controlled
                        hideButton
                        onChange={(event) => {
                            setNewKeywordValue(event.target.value);
                        }}
                        onSubmit={debounce((keyword) => {
                            toggleFilterTerm(keyword);
                            setNewKeywordValue('');
                        }, 300)}
                        placeholder={intl.formatMessage({
                            id: 'filter_by_keyword',
                        })}
                        small
                        value={newKeywordValue}
                        wide
                    />
                </StyledSearchInput>
            )}
            {totalFound > 0 ? facetElements : null}
            {showAdContainer && (
                <AdContainer
                    isLandingPage={isLandingPage}
                    pageName={pageName}
                    pathname={pathname}
                    queryParams={queryParams}
                />
            )}
        </StyledFacetWrapper>
    );

    if (Boolean(totalFound) && !isMobileView) {
        return <StyledDesktopWrapper className={className}>{facetWrapper}</StyledDesktopWrapper>;
    }

    if (!isMobileView) {
        return null;
    }

    return (
        <>
            <Button
                iconLeft={SlidersHorizontal}
                onClick={handleDrawerOpen}
                variant="outlined"
            >
                <FormattedMessage id="searchPage.mobileHeader.title" />
            </Button>
            <Drawer
                actions={
                    <Button
                        onClick={handleClearAll}
                        variant="outlined"
                    >
                        <FormattedMessage id="searchPage.clearAll" />
                    </Button>
                }
                anchor="right"
                data-testid="slide-menu"
                handleClose={handleDrawerClose}
                headerText={intl.formatMessage({ id: 'searchPage.mobileHeader.title' })}
                show={isDrawerOpen}
                size="extended"
            >
                {facetWrapper}
            </Drawer>
        </>
    );
};

export default memo<SearchFacetsProps>(SearchFacets, isEqual);

const SortAndKeywordsContainer = styled.div`
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const FilterBar = styled.div<{ $displaySortBy: boolean; $empty: boolean }>`
    ${({ $displaySortBy, $empty }) => css`
        margin-top: ${$displaySortBy && !$empty ? 32 : 0}px;
        // this counteracts the margin from filterBar that is otherwise extremely difficult to remove without
        // affecting the search results view
        margin-bottom: ${$displaySortBy ? 16 : 0}px;
    `}
`;

const ResultsAndSearchContainer = styled.div`
    margin-bottom: 16px;
`;

const ResultsCountRow = styled.div`
    margin-bottom: 16px;
`;

const ResultsCountLabel = styled(H5)`
    font-size: 16px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        color: ${({ theme }) => theme.colors.steel};
    }
`;

const StyledSearchInput = styled.div`
    height: 30px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-bottom: 16px;
    }
`;

const mobileFacetWrapperFragment = css`
    background-color: ${({ theme }) => theme.colors.white};
    padding: 13px 20px 20px 20px;
    overflow: auto;

    [class*='taxonomy-facet'][class*='categories'] {
        padding-top: 0;
    }
`;

const desktopFacetWrapperFragment = css`
    flex: 0 0 10%;
    min-width: 282px;
    display: block;
`;

const StyledFacetWrapper = styled.div<{ $isMobileFacets: boolean }>`
    ${({ $isMobileFacets }) => ($isMobileFacets ? mobileFacetWrapperFragment : desktopFacetWrapperFragment)}
`;

const StyledDesktopWrapper = styled.div`
    position: relative;
    margin-right: 24px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        flex: 0 0 10%;
        min-width: 282px;
    }
`;
